import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import './ShareModal.css';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { toLonLat } from 'ol/proj';
import { ServerTypeHelper } from '../../../../_config/helpers';
import { Button } from 'primereact/button';


const isLocalDev = () =>
  typeof window !== 'undefined' &&
  window.location.hostname === 'localhost' &&
  window.location.port === '3000' &&
  window.location.pathname.startsWith('/portal');

export const PEDEA_URL = isLocalDev()
  ? 'http://localhost:3000/portal'
  : 'https://hgpedea.sema.ce.gov.br';

export default function ShareModal({ map, active, callback }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [tab, setTab] = useState('link');
  const [copied, setCopied] = useState(false);
  const [qrSrc, setQrSrc] = useState('');
  const [shareUrl, setShareUrl] = useState(PEDEA_URL);
  const inputRef = useRef(null);

  useEffect(() => { if (active) setShow(true); }, [active]);
  useEffect(() => { callback(show); }, [show, callback]);

  // Gera o QR como dataURL (PNG)
  useEffect(() => {
    let isMounted = true;
    QRCode.toDataURL(shareUrl, {
      width: 256,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' }
    })
      .then(url => { if (isMounted) setQrSrc(url); })
      .catch(() => { if (isMounted) setQrSrc(''); });
    return () => { isMounted = false; };
  }, [shareUrl]);

  /** Util */
  const round = (n, p = 5) => Number.parseFloat(n).toFixed(p);

  // Achata a árvore de layers (grupos) em um array plano, na ordem de desenho.
  function flattenLayers(map) {
    const out = [];
    const walk = (lyr) => {
      const children = lyr.getLayers?.();
      if (children?.getArray) {
        children.getArray().forEach(walk);
      } else {
        out.push(lyr);
      }
    };
    map.getLayers().getArray().forEach(walk);
    return out;
  }

  function isShareableLayer(layer) {
    if (!layer?.get) return false;

    // Nunca compartilhar se estiver travada
    if (layer.get('lockedVisibility')) return false;

    // Não compartilhar base (mantém comportamento estável no restore)
    if (layer.get('isBaseLayer')) return false;

    // Compartilhar somente o que é reconstruível:
    //  - precisa ter 'name' (identificador no catálogo)
    //  - serverType GEOSERVER (case-insensitive)
    const name = layer.get('title');

    return !!name && layer.get('serverType') === ServerTypeHelper.GEOSERVER;
  }


  // Gera a lista de índices visíveis (para usar na *codificação* da URL)
  function buildVisibleLayerIndexes(map) {
    const flat = flattenLayers(map);
    const idxs = [];
    flat.forEach((lyr, i) => {
      if (!isShareableLayer(lyr)) return;
      if (lyr.getVisible?.()) idxs.push(i);
    });
    return idxs;
  }


  function buildDeepLink() {
    if (!map) return PEDEA_URL;

    const view = map.getView?.();
    const size = map.getSize?.();
    if (!view || !size || size[0] === 0 || size[1] === 0) return PEDEA_URL;

    const extent = view.calculateExtent(size, 'EPSG:4326'); // EPSG:3857
    const [minx, miny, maxx, maxy] = extent;

    const center = view.getCenter?.();
    const centerLonLat = center ? toLonLat(center) : [0, 0];
    const zoom = view.getZoom?.() ?? 0;
    const rotation = view.getRotation?.() || 0;

    // camadas: índices do array achatado
    const idxs = buildVisibleLayerIndexes(map); // ex.: [0,3,8,12]
    const lParam = idxs.join(','); // números, não precisa encode

    const url =
      `${PEDEA_URL}/?` +
      `l=${lParam}` +
      `&bbox=${round(minx, 2)},${round(miny, 2)},${round(maxx, 2)},${round(maxy, 2)}` +
      `&z=${round(zoom, 2)}` +
      `&c=${round(centerLonLat[0], 5)},${round(centerLonLat[1], 5)}` +
      `&r=${round(rotation, 3)}`;

    return url;
  }

  /** Atualiza o link quando o mapa muda ou visibilidades mudam */
  useEffect(() => {
    if (!map) return;
    const update = () => setShareUrl(buildDeepLink());
    update(); // inicial

    // move/zoom/rotate
    map.on('moveend', update);

    // toggle de visibilidade nas layers (recursivo)
    const all = [];
    const walk = (layer) => {
      all.push(layer);
      const group = layer.getLayers?.();
      if (group && group.getArray) group.getArray().forEach(walk);
    };
    map.getLayers().getArray().forEach(walk);
    all.forEach(l => l.on('change:visible', update));

    return () => {
      map.un('moveend', update);
      all.forEach(l => l.un('change:visible', update));
    };
  }, [map]);
  // tamanho da popup para desktop
  const POPUP_FEATURES = 'width=720,height=640,top=100,left=100,menubar=no,toolbar=no,status=no';

  // detecção simples p/ WhatsApp mobile vs desktop
  const isMobile = () => /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);

  // monta os links de compartilhamento
  function buildShareLinks({ url, text, via }) {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(text || '');
    const v = via ? encodeURIComponent(via) : '';

    return {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      x: `https://twitter.com/intent/tweet?url=${u}${t ? `&text=${t}` : ''}${v ? `&via=${v}` : ''}`,
      whatsapp: (isMobile()
        ? `https://api.whatsapp.com/send?text=${t ? `${t}%20` : ''}${u}`
        : `https://web.whatsapp.com/send?text=${t ? `${t}%20` : ''}${u}`),
      telegram: `https://t.me/share/url?url=${u}${t ? `&text=${t}` : ''}`,
    };
  }

  function openPopup(href) {
    // tenta usar Web Share API no mobile
    if (navigator.share && isMobile()) {
      // fallback para popup se der erro
      navigator.share({ url: href }).catch(() => window.open(href, '_blank', POPUP_FEATURES));
      return;
    }
    window.open(href, '_blank', POPUP_FEATURES);
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const dialogFooter = (
  <div className="ShareModalFooter">
    <Button
      raised
      icon="pi pi-times"
      className="CloseBtn"
      onClick={() => setShow(false)}
    >
      {t('common.close') || 'Fechar'}
    </Button>
  </div>
);

return (
  <Dialog
    visible={show}
    onHide={() => setShow(false)}
    header={t('share.title') || 'Compartilhamento'}
    className="ShareModal"
    modal
    draggable={false}
    resizable={false}
    footer={dialogFooter}
    dismissableMask={false}
    closable={true}
  >
    <div className="ShareModalContent">

    

      <div className="ShareModalTabs">
        <button
          type="button"
          className={`ShareModalTab ${tab === 'link' ? 'active' : ''}`}
          onClick={() => setTab('link')}
        >
          <i className="fa-solid fa-link" />
          <span>
            {t('share.tab.link') || 'Link'}
          </span>
        </button>

        <button
          type="button"
          className={`ShareModalTab ${tab === 'social' ? 'active' : ''}`}
          onClick={() => setTab('social')}
        >
          <i className="fa-solid fa-share-nodes" />
          <span>
            {t('share.tab.social') || 'Social'}
          </span>
        </button>
      </div>



      <div className="ShareModalBody">

        {tab === 'link' && (
          <div className="share-link-view">

            <label className="ShareModalLabel">
              {t('share.viaDirect') || 'Via link direto'}
            </label>

            <div className="ShareLinkInput">

              <input
                ref={inputRef}
                type="text"
                value={shareUrl}
                readOnly
              />

              <button
                type="button"
                className={`copy-btn ${copied ? 'copied' : ''}`}
                onClick={copyToClipboard}
                title={t('share.copy') || 'Copiar'}
              >
                <i className="material-icons">
                  {copied ? 'check' : 'content_copy'}
                </i>
              </button>

            </div>



            <div className="ShareQRCode">

              <h6>
                <i className="fa-solid fa-qrcode" />
                QR Code
              </h6>

              {qrSrc ? (
                <div className="ShareQRCodeImage">
                  <img
                    src={qrSrc}
                    alt="QR code"
                  />
                </div>
              ) : (
                <div className="ShareQRCodeLoading">
                  Gerando QR…
                </div>
              )}

            </div>

          </div>
        )}



        {tab === 'social' && (
          <div className="share-social-view">

            <p className="ShareSocialHint">
              {t('share.socialHint') ||
                'Compartilhe diretamente nas redes:'}
            </p>

            {(() => {

              const text =
                t('share.defaultText') ||
                'Veja este mapa na PEDEA';

              const links = buildShareLinks({
                url: shareUrl,
                text,
                via: 'PEDEA'
              });

              return (

                <div className="social-grid">

              
                  <button
                    className="btn-social btn-li"
                    onClick={() => openPopup(links.linkedin)}
                    aria-label="LinkedIn"
                    title="LinkedIn"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      <path d="M20.45 3H3.55A.55.55 0 0 0 3 3.55v16.9c0 .3.24.55.55.55h16.9c.3 0 .55-.24.55-.55V3.55A.55.55 0 0 0 20.45 3ZM8.3 18.5H5.7V9.9h2.6v8.6ZM7 8.66a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm12.3 9.84h-2.6v-4.69c0-1.12-.02-2.56-1.56-2.56-1.57 0-1.81 1.23-1.81 2.48v4.77h-2.6V9.9h2.5v1.18h.04c.35-.66 1.22-1.36 2.5-1.36 2.67 0 3.16 1.76 3.16 4.05v4.73Z" />
                    </svg>
                  </button>


             
                  <button
                    className="btn-social btn-fb"
                    onClick={() => openPopup(links.facebook)}
                    aria-label="Facebook"
                    title="Facebook"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      <path d="M22 12a10 10 0 1 0-11.57 9.9v-7.02H7.9V12h2.53V9.79c0-2.5 1.49-3.89 3.78-3.89 1.1 0 2.26.2 2.26.2v2.48h-1.27c-1.25 0-1.63.78-1.63 1.58V12h2.78l-.44 2.88h-2.34v7.02A10 10 0 0 0 22 12Z" />
                    </svg>
                  </button>


               
                  <button
                    className="btn-social btn-x"
                    onClick={() => openPopup(links.x)}
                    aria-label="X"
                    title="X"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      <path d="M18.244 3H21L14.48 10.43 22.5 21h-6.18l-4.83-6.33L5.88 21H3l6.99-7.9L2.25 3h6.29l4.37 5.86L18.244 3Zm-.96 16.29h1.69L7.18 4.64H5.41l11.874 14.65Z" />
                    </svg>
                  </button>


                  <button
                    className="btn-social btn-wa"
                    onClick={() => openPopup(links.whatsapp)}
                    aria-label="WhatsApp"
                    title="WhatsApp"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      <path d="M12 2.004C6.478 2.004 2 6.48 2 12c0 1.77.46 3.43 1.27 4.88L2 22l5.22-1.27A9.96 9.96 0 0 0 12 22c5.522 0 10-4.477 10-10s-4.478-9.996-10-9.996Zm0 2c4.41 0 8 3.588 8 7.996S16.41 20 12 20a7.95 7.95 0 0 1-3.77-.95l-.27-.14-3.05.74.74-3.05-.14-.27A7.96 7.96 0 0 1 4 12c0-4.408 3.59-7.996 8-7.996Z" />

                      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.11-.59.11-.11.29-.33.45-.52.15-.19.2-.31.29-.49.1-.2.05-.37-.01-.49-.06-.12-.55-1.31-.75-1.79-.2-.48-.41-.42-.55-.42l-.57-.01c-.2 0-.52.07-.79.34-.27.27-.83.85-.83 2.08s.86 2.37.98 2.53c.12.16 1.66 2.52 4.03 3.55.71.31 1.26.49 1.7.63.71.22 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.43.26-.71.26-1.29.19-1.41-.07-.12-.23-.19-.51-.33Z" />
                    </svg>
                  </button>


               
                  <button
                    className="btn-social btn-tg"
                    onClick={() => openPopup(links.telegram)}
                    aria-label="Telegram"
                    title="Telegram"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      fill="currentColor"
                    >
                      <path d="M21.74 4.1c.64-.25 1.27.33 1.14 1.02l-2.52 13.35c-.12.69-.9.98-1.44.58l-4.76-3.5-2.6 2.99c-.4.46-1.17.33-1.39-.26l-1.8-4.73-4.63-1.79c-.71-.27-.71-1.27 0-1.54L21.74 4.1Zm-8.34 11.11 3.85 2.82 2.02-9.94-5.87 4.48-2.46-1.22-2.46-1.22.46 2.26 2 .6Z" />
                    </svg>
                  </button>

                </div>

              );

            })()}

          </div>
        )}

      </div>

    </div>
  </Dialog>
);
}
